import React from 'react';
import { Page, Text, View, Image, StyleSheet } from '@react-pdf/renderer';
import { PDF_COLORS } from '../pdfColors';
import { LOGO_GENERALITAT, LOGO_UTE } from '../reportAssets';
import i18n from '../../../i18n';

const styles = StyleSheet.create({
  page: {
    backgroundColor: PDF_COLORS.white,
    padding: 0,
    fontFamily: 'Poppins',
  },
  topBand: {
    height: 6,
  },
  // Cabecera institucional: Generalitat (client) izq · UTE C-25 (emissor) der
  logoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    paddingHorizontal: 48,
    paddingTop: 18,
    paddingBottom: 28,
  },
  logoGeneralitat: {
    height: 46,
    maxWidth: 240,
    objectFit: 'contain',
  },
  logoUte: {
    height: 60,
    maxWidth: 220,
    objectFit: 'contain',
  },
  divider: {
    height: 0.75,
    backgroundColor: PDF_COLORS.greyMid,
    marginHorizontal: 48,
  },
  content: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 56,
    paddingVertical: 40,
  },
  title: {
    fontSize: 24,
    fontFamily: 'Poppins',
    fontWeight: 600,
    color: PDF_COLORS.ink,
    textAlign: 'center',
    marginBottom: 14,
    letterSpacing: 0.3,
  },
  titleRule: {
    width: 60,
    height: 2.5,
    marginBottom: 18,
  },
  meta: {
    fontSize: 10,
    fontFamily: 'Poppins',
    color: PDF_COLORS.greyDark,
    textAlign: 'center',
    marginBottom: 6,
  },
  filtersSection: {
    marginTop: 26,
    paddingHorizontal: 40,
    alignItems: 'center',
  },
  filtersTitle: {
    fontSize: 11,
    fontFamily: 'Poppins',
    fontWeight: 600,
    color: PDF_COLORS.ink,
    textAlign: 'center',
    marginBottom: 8,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  filterRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginBottom: 3,
  },
  filterLabel: {
    fontSize: 10,
    fontFamily: 'Poppins',
    fontWeight: 600,
    color: PDF_COLORS.ink,
  },
  filterValue: {
    fontSize: 10,
    fontFamily: 'Poppins',
    color: PDF_COLORS.greyDark,
  },
  footerBrand: {
    textAlign: 'center',
    fontSize: 8,
    fontFamily: 'Poppins',
    color: PDF_COLORS.greyDark,
    marginBottom: 16,
  },
  bottomLine: {
    height: 4,
  },
});

interface CoverPageProps {
  title: string;
  typeColor: string;
  generatedAt: string;
  totalCount: number;
  filtersSummary?: { label: string; value: string }[];
}

export const CoverPage = ({
  title,
  typeColor,
  generatedAt,
  totalCount,
  filtersSummary,
}: CoverPageProps) => (
  <Page size="A4" style={styles.page}>
    <View style={[styles.topBand, { backgroundColor: typeColor }]} />

    <View style={styles.content}>
      <Text style={styles.title}>{title}</Text>
      <View style={[styles.titleRule, { backgroundColor: typeColor }]} />
      <Text style={styles.meta}>{generatedAt}</Text>
      <Text style={styles.meta}>
        {i18n.t('tables:pdf.records', {
          count: totalCount,
          defaultValue: `${totalCount} registres`,
        })}
      </Text>
      {filtersSummary && filtersSummary.length > 0 ? (
        <View style={styles.filtersSection}>
          <Text style={styles.filtersTitle}>
            {i18n.t('tables:pdf.appliedFilters', {
              defaultValue: 'Filtres aplicats',
            })}
          </Text>
          {filtersSummary.map((filter, idx) => (
            <View key={idx} style={styles.filterRow}>
              <Text style={styles.filterLabel}>{filter.label}: </Text>
              <Text style={styles.filterValue}>{filter.value}</Text>
            </View>
          ))}
        </View>
      ) : null}
    </View>

    {/* Logos institucionales al peu de la portada */}
    <View style={styles.divider} />
    <View style={styles.logoRow}>
      <Image src={LOGO_GENERALITAT} style={styles.logoGeneralitat} />
      <Image src={LOGO_UTE} style={styles.logoUte} />
    </View>
    <View style={[styles.bottomLine, { backgroundColor: typeColor }]} />
  </Page>
);
